import { useQuery } from "@tanstack/react-query";
import { CircleSlash } from "lucide-react";

import { asBoolean, asString } from "@/lib/tina/value-readers";
import { useEventRepository } from "@/pages/events/hooks/use-event-repository";
import { EntityCard } from "@/pages/tina/items/components/entity-card";
import {
  KeyValueGrid,
  type KeyValueRow,
} from "@/pages/tina/items/components/key-value-grid";
import { PanelShell } from "@/pages/tina/items/components/panel-shell";
import type { PipelineItem } from "@/types/tina";

interface DupMeta {
  duplicate_of_event_id?: string;
  duplicate_confidence?: number;
  duplicate_match_type?: "rule" | "gemini" | "gemini_same_act";
  admin_event_id?: string;
  creation_group_id?: string;
  creation_guard_matched_key?: string;
  idempotency_reused?: boolean | string;
}

// Match types whose raw value does not read on its own. `gemini_same_act` is Tina's late_dup
// match on the same act at ANOTHER venue in the same city.
const MATCH_TYPE_LABELS: Record<string, string> = {
  gemini_same_act: "same act, other venue",
};

function formatConfidence(value: unknown): string {
  if (typeof value !== "number") return "—";
  return value.toFixed(2);
}

export function DuplicatePanel({ item }: { item: PipelineItem }) {
  const meta = (item.metadata ?? {}) as DupMeta;
  const dupId = asString(meta.duplicate_of_event_id);
  const adminEventId =
    asString(meta.admin_event_id) ?? item.created_event_id ?? undefined;
  const creationGroupId = asString(meta.creation_group_id);
  const creationGuardKey = asString(meta.creation_guard_matched_key);
  const isCreationGuardReuse = asBoolean(meta.idempotency_reused) ?? false;
  const hasCreationGuardInfo =
    isCreationGuardReuse || !!creationGroupId || !!creationGuardKey;
  const displayEventId =
    dupId ?? (hasCreationGuardInfo ? adminEventId : undefined);

  const repo = useEventRepository();
  const dupQuery = useQuery({
    queryKey: ["events", displayEventId],
    queryFn: () => repo.get(displayEventId as string),
    enabled: !!displayEventId,
  });

  if (!displayEventId && !hasCreationGuardInfo) {
    return (
      <PanelShell
        status="duplicate"
        subtitle={`Detected at ${item.current_stage ?? "—"}.`}
      >
        <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <CircleSlash className="h-4 w-4" />
          No duplicate match info on this item.
        </p>
      </PanelShell>
    );
  }

  const eventName = (dupQuery.data as { name?: string } | undefined)?.name;
  const matchType = hasCreationGuardInfo
    ? "creation_guard"
    : meta.duplicate_match_type
      ? (MATCH_TYPE_LABELS[meta.duplicate_match_type] ??
        meta.duplicate_match_type)
      : "unknown";
  const rows: KeyValueRow[] = [
    { label: "Stage", value: item.current_stage ?? "—", mono: true },
    { label: "Match type", value: matchType, mono: true },
    ...(hasCreationGuardInfo
      ? [
          {
            label: "Admin event",
            value: adminEventId ?? "—",
            mono: true,
          },
          {
            label: "Creation group",
            value: creationGroupId ?? "—",
            mono: true,
          },
          {
            label: "Matched key",
            value: creationGuardKey ? (
              <TruncatedMono value={creationGuardKey} />
            ) : (
              "—"
            ),
          },
        ]
      : [
          {
            label: "Confidence",
            value: formatConfidence(meta.duplicate_confidence),
          },
        ]),
  ];

  return (
    <PanelShell
      status="duplicate"
      subtitle={
        hasCreationGuardInfo
          ? `Detected at ${item.current_stage ?? "—"} · reused existing admin event`
          : `Detected at ${item.current_stage ?? "—"} · ${matchType} match`
      }
    >
      <KeyValueGrid rows={rows} />
      {displayEventId && (
        <EntityCard
          label={hasCreationGuardInfo ? "Reused admin event" : "Duplicate of"}
          href={`/events/edit/${displayEventId}`}
          isLoading={dupQuery.isLoading}
          name={eventName}
          id={displayEventId}
        />
      )}
    </PanelShell>
  );
}

function TruncatedMono({ value }: { value: string }) {
  return (
    <span
      className="block max-w-full truncate font-mono text-[11px]"
      title={value}
    >
      {value}
    </span>
  );
}
